import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

const Question = ({ question, onAnswer, selectedAnswer, isCompleted, getAnswerButtonStyle, questionNumber }) => {
    const remarkPlugins = [[remarkMath]];
    const rehypePlugins = [[rehypeKatex, { strict: false }]];

    // All preprocessing has been removed. Ensure data from the database
    // has backslashes escaped (e.g., `$\\frac{1}{3}$`) to prevent
    // corruption by JavaScript's string interpreter.

    // Determine the best layout based on question properties
    const getLayoutClass = () => {
        if (!question.imageUrl) return '';

        // If image URL contains specific keywords indicating diagram/graph
        const isDiagram = /diagram|graph|chart|plot|geometry|figure/i.test(question.imageUrl);
        
        // If question text is short (less than 100 characters)
        const isShortText = question.questionText.length < 100;
        
        // If question text has multiple paragraphs
        const hasMultipleParagraphs = question.questionText.split('\n\n').length > 1;

        if (isDiagram || (isShortText && !hasMultipleParagraphs)) {
            return 'image-above'; // Show image above for diagrams or short questions
        }

        if (hasMultipleParagraphs && !isShortText) {
            return 'image-side-by-side'; // Use side-by-side for longer, structured content
        }

        return ''; // Default to right-aligned float for normal cases
    };

    return (
        <div className="question-container">
            {/* Question Content */}
            <div className="question-content">
                <div className={`question-text-with-image ${getLayoutClass()}`}>
                    <div className="question-header">
                        {questionNumber && (
                            <span className="question-number">{questionNumber}</span>
                        )}
                        <div className="question-text-wrapper">
                            <div className="question-text">
                                <ReactMarkdown
                                    remarkPlugins={remarkPlugins}
                                    rehypePlugins={rehypePlugins}
                                >
                                    {question.questionText}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </div>
                    {question.imageUrl && (
                        <div className="question-image-container">
                            <img 
                                src={question.imageUrl} 
                                alt="Question illustration"
                                className="question-image"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    console.error('Failed to load image:', question.imageUrl);
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Answer Options */}
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 'var(--spacing-sm)',
                ...(question.options.some(option => option.optionUrl) && {
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--spacing-md)'
                })
            }}>
                {question.options.map((option) => (
                    <button
                        key={option.optionId}
                        onClick={() => onAnswer(question.questionId, option)}
                        className="button secondary"
                        style={getAnswerButtonStyle(question, option)}
                        disabled={isCompleted}
                    >
                        {option.optionUrl && (
                            <img
                                src={option.optionUrl}
                                alt="Option illustration"
                                style={{
                                    maxWidth: '180px',
                                    maxHeight: '120px',
                                    marginBottom: '8px',
                                    objectFit: 'contain',
                                    borderRadius: '6px',
                                    background: '#f8f8f8',
                                    display: 'block'
                                }}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        )}
                        <ReactMarkdown
                            remarkPlugins={remarkPlugins}
                            rehypePlugins={rehypePlugins}
                        >
                            {option.optionText}
                        </ReactMarkdown>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default Question; 